import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_FLAG = 'supervision.chunkReloaded';

/**
 * Core retry policy, exported for tests. On a chunk-load failure (stale hashed asset
 * after a deploy) it sets a sessionStorage flag and reloads once; if the flag is
 * already set the failure is real — rethrow instead of reload-looping. On success the
 * flag is cleared so the next deploy gets its own single retry.
 */
export async function attemptImportWithRetry<T>(
  importFn: () => Promise<T>,
  reload: () => void = () => window.location.reload(),
): Promise<T> {
  try {
    const mod = await importFn();
    window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    return mod;
  } catch (err) {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      throw err;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    reload();
    // No error boundary exists in this app, so the import must never reject on the
    // reload path — a rejected lazy() would unmount the entire React root (white
    // screen flash) in the instant before the reload lands. Return a never-settling
    // promise so Suspense keeps showing its fallback until the page unloads.
    return new Promise<T>(() => undefined);
  }
}

/** React.lazy with the stale-chunk retry policy. Use for COLD routes only. */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => attemptImportWithRetry(importFn));
}
